import { ORGANISATION_NAME_MAX_LENGTH } from 'constants/validationConstants';

// Page titles
export const CHECKEMAIL_PAGE_TITLE = 'CheckEmail';
export const DASHBOARD_PAGE_TITLE = 'Dashboard';
export const FARMS_PAGE_TITLE = 'Farms';
export const FORGOT_PASSWORD_PAGE_TITLE = 'Forgot Password';
export const GLOBAL_CATALOGUE_PAGE_TITLE = 'Global Catalogue';
export const HOME_PAGE_TITLE = 'Home';
export const LIME_MATE_PAGE_TITLE = 'LimeMate';
export const LOGIN_PAGE_TITLE = 'Login';
export const PROFILE_PAGE_TITLE = 'Profile';
export const FARM_PLAN_PREVIEW_PAGE_TITLE = 'Farm Plan Preview';
export const PADDOCK_PLAN_PREVIEW_PAGE_TITLE = 'Paddock Plan Preview';
export const RESET_PASSWORD_PAGE_TITLE = 'Reset Password';
export const SIGNUP_PAGE_TITLE = 'Signup';
export const VERIFICATION_PAGE_TITLE = 'Verification';
export const ORGANISATIONS_PAGE_TITLE = 'Organisations';

// Reference data for dropdowns
export const AU_STATES = [
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
];

export const ADVISER_DATA_INDEX = 'adviser';
export const FARMER_DATA_INDEX = 'farmer';
export const BOTH_DATA_INDEX = 'both';

export const USER_ROLES = [
  { value: ADVISER_DATA_INDEX, label: 'Adviser' },
  { value: FARMER_DATA_INDEX, label: 'Farmer' },
  { value: BOTH_DATA_INDEX, label: 'Both (Adviser + Farmer)' },
];

// Farms page — sidebar layout (all widths in rem)
export const SIDEBAR_MIN_WIDTH = 15; // rem ≈ 240px at 16px base
export const SIDEBAR_MAX_WIDTH = 30; // rem ≈ 480px at 16px base
export const SIDEBAR_INITIAL_WIDTH = 18; // rem ≈ 288px at 16px base
export const SIDEBAR_WIDTH_STORAGE_KEY = 'farm-sidebar-width';
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'farm-sidebar-collapsed';
export const SIDEBAR_INFO_BANNER_STORAGE_KEY = 'farm-sidebar-info-banner';
export const SUMMARY_SCOPE_HINT_STORAGE_KEY = 'insights-summary-scope-hint';
export const AREA_UNIT = 'ha';

// Farm tree — year filter sentinel
export const TREE_YEAR_ALL = 'all' as const;
export const TREE_YEAR_ALL_LABEL = 'All years' as const;

// Farm tree — grouping mode
export const TREE_GROUP_BY_FARM = 'farmPaddock' as const;
export const TREE_GROUP_BY_CROP = 'cropType' as const;
// Keyed by the union rather than `string`, so a missing entry is a type error
// instead of rendering "undefined".
export const TREE_GROUP_BY_LABELS: Record<
  typeof TREE_GROUP_BY_FARM | typeof TREE_GROUP_BY_CROP,
  string
> = {
  [TREE_GROUP_BY_FARM]: 'Farm',
  [TREE_GROUP_BY_CROP]: 'Crop',
};
// Bucket for plans whose header carries no crop — a real state, since
// buildBlankInput seeds cropName as an empty string.
export const TREE_UNCROPPED_LABEL = 'Uncropped';

// Input document — tab and lens identifiers
export const INPUT_DOC_TAB_PLANNING = 'planning' as const;
export const INPUT_DOC_TAB_INSIGHTS = 'insights' as const;
export const INPUT_DOC_TAB_DIAGNOSTICS = 'diagnostics' as const;
export const INPUT_DOC_TAB_MAP = 'map' as const;
export const INPUT_DOC_LENS_SUMMARY = 'summary' as const;
export const INPUT_DOC_LENS_SUMMARY_2 = 'summary2' as const;
export const INPUT_DOC_LENS_MARGIN_BREAK_EVEN = 'marginBreakEven' as const;
// Pre-OP-2393 URL value for the Margin & break-even lens — still accepted so
// old bookmarks and shared links resolve to the renamed lens.
export const INPUT_DOC_LENS_GROSS_MARGIN_LEGACY = 'grossMargin' as const;
export const INPUT_DOC_LENS_ANALYSIS = 'analysis' as const;

// Input document — tab labels
export const INPUT_DOC_TAB_LABELS: Record<
  | typeof INPUT_DOC_TAB_PLANNING
  | typeof INPUT_DOC_TAB_INSIGHTS
  | typeof INPUT_DOC_TAB_DIAGNOSTICS
  | typeof INPUT_DOC_TAB_MAP,
  string
> = {
  [INPUT_DOC_TAB_PLANNING]: 'Planning',
  [INPUT_DOC_TAB_INSIGHTS]: 'Insights',
  [INPUT_DOC_TAB_DIAGNOSTICS]: 'Farm Diagnostics',
  [INPUT_DOC_TAB_MAP]: 'Map',
};

// Farm Diagnostics tab — empty states
export const FARM_DIAGNOSTICS_NO_PADDOCK_MESSAGE =
  'Select a paddock in the farm tree to see its year-by-year trends.';
export const FARM_DIAGNOSTICS_NO_DATA_MESSAGE =
  'No multi-year data available for this paddock';

// Farm Diagnostics — sub-dashboard (lens) identifiers. The Yearly Overview
// table lives outside the lens switcher entirely, shared by the three
// per-paddock lenses below (not Rotation
// Efficiency, which operates at Farm/Org level and needs no paddock).
export const FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY =
  'rotationEfficiency' as const;
export const FARM_DIAGNOSTICS_LENS_WUE_TREND = 'wueTrend' as const;
export const FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY =
  'financialEfficiency' as const;
export const FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL = 'bioVsFinancial' as const;

export const FARM_DIAGNOSTICS_LENS_LABELS: Record<
  | typeof FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY
  | typeof FARM_DIAGNOSTICS_LENS_WUE_TREND
  | typeof FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY
  | typeof FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL,
  string
> = {
  [FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY]: 'Rotation Efficiency',
  [FARM_DIAGNOSTICS_LENS_WUE_TREND]: 'WUE Trend',
  [FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY]: 'Financial Efficiency',
  [FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL]: 'Bio vs Financial',
};

// Farm Diagnostics — trend direction labels (WUE / Financial WUE rolling-average comparison)
export const FARM_DIAGNOSTICS_TREND_IMPROVING = 'improving' as const;
export const FARM_DIAGNOSTICS_TREND_STABLE = 'stable' as const;
export const FARM_DIAGNOSTICS_TREND_DECLINING = 'declining' as const;

export const FARM_DIAGNOSTICS_TREND_LABELS: Record<
  | typeof FARM_DIAGNOSTICS_TREND_IMPROVING
  | typeof FARM_DIAGNOSTICS_TREND_STABLE
  | typeof FARM_DIAGNOSTICS_TREND_DECLINING,
  string
> = {
  [FARM_DIAGNOSTICS_TREND_IMPROVING]: 'Improving',
  [FARM_DIAGNOSTICS_TREND_STABLE]: 'Stable',
  [FARM_DIAGNOSTICS_TREND_DECLINING]: 'Declining',
};

// Farm Diagnostics — WUE Trend / Financial Efficiency / Bio vs Financial copy
export const FARM_DIAGNOSTICS_ROLLING_AVG_UNAVAILABLE_MESSAGE =
  'Rolling average needs 3 consecutive years of data';
export const FARM_DIAGNOSTICS_ENTER_RAINFALL_MESSAGE =
  'Enter rainfall for this year to calculate WUE';
export const FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE =
  'Not enough data yet — review or add rainfall and yield above in Yearly Overview.';
export const FARM_DIAGNOSTICS_YIELD_UNIT_NOT_CONVERTIBLE_MESSAGE =
  "Yield unit can't be converted to kg — WUE Index unavailable for this year";

export const FARM_YEAR_INPUTS_PANEL_TITLE = 'Farm-Level Inputs';
export const FARM_YEAR_INPUTS_RAINFALL_LABEL = 'Rainfall (mm)';
export const FARM_YEAR_INPUTS_RAINFALL_REQUIRED_TOOLTIP =
  'Rainfall is required to calculate WUE for this year';
export const FARM_YEAR_INPUTS_PRACTICE_LABEL = 'Management Practice';
export const FARM_YEAR_INPUTS_COLLAPSED_HINT =
  'Expand to enter rainfall and management practice for each year';
export const FARM_YEAR_INPUTS_COPY_FROM_ABOVE_LABEL = 'Copy from above';
export const FARM_YEAR_INPUTS_COPY_FROM_ABOVE_TOOLTIP =
  "Copy the farm above's rainfall and management practice into this farm, for any matching year";

export const FARM_DIAGNOSTICS_YIELD_FIELD_LABEL = 'Yield';
export const FARM_DIAGNOSTICS_RESET_YEAR_LABEL = 'Reset';
export const FARM_DIAGNOSTICS_RESET_ALL_LABEL = 'Reset All';
export const FARM_DIAGNOSTICS_SOURCE_TOOLTIP =
  'Defaults to Actual when it has been recorded for that year, otherwise falls back to Plan. Switch to compare against the other directly.';
export const FARM_DIAGNOSTICS_TOTAL_COST_TOOLTIP =
  'Sum of cost categories — enable them in Columns to edit';
export const FARM_DIAGNOSTICS_TABLE_COLLAPSED_HINT =
  'Expand to view and edit yearly rainfall, management practice, yield, cost and Plan/Actual data';
export const FARM_DIAGNOSTICS_TABLE_SHARED_SOURCE_SUBTITLE =
  'Shared across WUE Trend, Financial Efficiency and Bio vs Financial below — editing a year here updates all three.';

// Farm Diagnostics — generic collapsible-section controls (Yearly Overview
// table, Farm-Level Inputs panel)
export const FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL = 'Expand';
export const FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL = 'Collapse';

// Farm Diagnostics — shared chart display-options control (year-on-year /
// 3-yr rolling avg / management-practice markers)
export const FARM_DIAGNOSTICS_DISPLAY_OPTIONS_TITLE = 'Chart Display';
export const FARM_DIAGNOSTICS_DISPLAY_YEAR_ON_YEAR_LABEL = 'Year-on-year';
export const FARM_DIAGNOSTICS_DISPLAY_ROLLING_AVG_LABEL = '3-yr rolling avg';
export const FARM_DIAGNOSTICS_DISPLAY_MARKERS_LABEL =
  'Management practice markers';

// Farm Diagnostics — trend factors tooltip (what changed since last year)
export const FARM_DIAGNOSTICS_TREND_FACTORS_HEADING = 'Since previous year';
export const FARM_DIAGNOSTICS_TREND_FACTORS_CAVEAT =
  "Reflects the most recent year's change, not the full 3-yr average";

export const FARM_DIAGNOSTICS_KEY_METRICS_LATEST_ROLLING_AVG_LABEL =
  'Latest 3-yr Rolling Avg';
export const FARM_DIAGNOSTICS_KEY_METRICS_TREND_LABEL = 'Trend';
export const FARM_DIAGNOSTICS_KEY_METRICS_YOY_CHANGE_LABEL =
  'Year-on-Year Change';

export const FARM_DIAGNOSTICS_COMPARISON_TABLE_ROW_LABELS = {
  financialWue: 'Financial WUE ($/mm)',
  gmPerHa: 'Gross Margin ($/ha)',
  wueIndex: 'WUE Index (kg/mm)',
};
export const FARM_DIAGNOSTICS_COMPARISON_COLUMN_3YR_AVG = '3-yr Avg';
export const FARM_DIAGNOSTICS_COMPARISON_COLUMN_LATEST = 'Latest Year';
export const FARM_DIAGNOSTICS_COMPARISON_COLUMN_CHANGE = 'Change';

// Farm Diagnostics — management practice (fixed, controlled enum; multi-select,
// not free text — Fallow/Pasture are mutually exclusive with each other and
// with the 3 active-cropping practices, see resolvePracticeTagsAfterChange).
export const FARM_DIAGNOSTICS_PRACTICE_NO_TILL = 'noTill' as const;
export const FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL =
  'conventionalTill' as const;
export const FARM_DIAGNOSTICS_PRACTICE_PASTURE = 'pasture' as const;
export const FARM_DIAGNOSTICS_PRACTICE_FALLOW = 'fallow' as const;
export const FARM_DIAGNOSTICS_PRACTICE_COVER_CROP = 'coverCrop' as const;

export const FARM_DIAGNOSTICS_PRACTICE_LABELS: Record<
  | typeof FARM_DIAGNOSTICS_PRACTICE_NO_TILL
  | typeof FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL
  | typeof FARM_DIAGNOSTICS_PRACTICE_PASTURE
  | typeof FARM_DIAGNOSTICS_PRACTICE_FALLOW
  | typeof FARM_DIAGNOSTICS_PRACTICE_COVER_CROP,
  string
> = {
  [FARM_DIAGNOSTICS_PRACTICE_NO_TILL]: 'No-till',
  [FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL]: 'Conventional till',
  [FARM_DIAGNOSTICS_PRACTICE_PASTURE]: 'Pasture',
  [FARM_DIAGNOSTICS_PRACTICE_FALLOW]: 'Fallow',
  [FARM_DIAGNOSTICS_PRACTICE_COVER_CROP]: 'Cover crop',
};
export const FARM_YEAR_INPUTS_PRACTICE_SELECT_PLACEHOLDER =
  'Select practice(s)';

// Farm Diagnostics — time range selector (shared across all four lenses)
export const FARM_DIAGNOSTICS_TIME_RANGE_LAST_3 = 'last3' as const;
export const FARM_DIAGNOSTICS_TIME_RANGE_LAST_5 = 'last5' as const;
export const FARM_DIAGNOSTICS_TIME_RANGE_ALL = 'all' as const;
export const FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM = 'custom' as const;

export const FARM_DIAGNOSTICS_TIME_RANGE_LABELS: Record<
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_LAST_3
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_LAST_5
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_ALL
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
  string
> = {
  [FARM_DIAGNOSTICS_TIME_RANGE_LAST_3]: 'Last 3 years',
  [FARM_DIAGNOSTICS_TIME_RANGE_LAST_5]: 'Last 5 years',
  [FARM_DIAGNOSTICS_TIME_RANGE_ALL]: 'All years',
  [FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM]: 'Custom range',
};
export const FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM_START_LABEL = 'From';
export const FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM_END_LABEL = 'To';

// Farm Diagnostics — Yearly Overview table: cost display mode
export const FARM_DIAGNOSTICS_COST_DISPLAY_TOTAL_LABEL = 'Total ($)';
export const FARM_DIAGNOSTICS_COST_DISPLAY_PER_HA_LABEL = '$/ha';
export const FARM_DIAGNOSTICS_CROP_LABEL = 'Crop';
export const FARM_DIAGNOSTICS_UNCROPPED_LABEL = 'Uncropped';

// Farm Diagnostics — trend chart legend (explains the thin year-on-year line
// vs the bold, trend-colored 3-yr rolling-average line)
export const FARM_DIAGNOSTICS_LEGEND_YEAR_ON_YEAR_LABEL = 'Year-on-year';
export const FARM_DIAGNOSTICS_LEGEND_ROLLING_AVG_LABEL = '3-yr rolling avg';

// Farm Diagnostics — Rotation Efficiency lens (Farm/Org-level paddock
// comparison over a user-picked date range — a comparison view, not a
// per-paddock trend, so it needs no paddock selected in the tree).
export const FARM_DIAGNOSTICS_ROTATION_NO_PADDOCKS_MESSAGE =
  'No paddocks found for this scope';
export const FARM_DIAGNOSTICS_ROTATION_SOURCE_GUIDANCE =
  "Each year uses Actual where any has been recorded, otherwise Plan. A year with Actual costs but no production recorded counts as 0 yield. Plan/Actual choices and yield or cost edits in a paddock's Yearly overview aren't applied here.";
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_PADDOCK = 'Paddock';
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_AREA = 'Area (ha)';
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_MANAGEMENT = 'Management';
// No "Rotation" prefix — the section is Rotation efficiency already; "avg"
// says these are averages over the selected years (design system rule).
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_WUE = 'WUE (kg/mm avg)';
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_GM = 'GM ($/ha avg)';
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_FINANCIAL_WUE =
  'Financial WUE ($/mm avg)';
// The report year's column — its crop is the plan being previewed.
export const buildRotationSummaryYearHeader = (
  year: number,
  isPlanYear: boolean
): string => (isPlanYear ? `${year} (plan)` : String(year));
export const FARM_DIAGNOSTICS_ROTATION_NO_DATA_TOOLTIP =
  'No data recorded for this paddock in the selected range';
export const FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_TREND = 'Trend';

export const FARM_DIAGNOSTICS_ROTATION_EXCLUDED_YEAR_REASON_LABELS: Record<
  'noData' | 'fallow' | 'pasture' | 'missingRainfall',
  string
> = {
  noData: 'No data recorded',
  fallow: 'Fallow',
  pasture: 'Pasture',
  missingRainfall: 'Rainfall not entered',
};
export const FARM_DIAGNOSTICS_ROTATION_EXCLUDED_YEARS_TOOLTIP_HEADING =
  'Years excluded from this rotation:';
export const FARM_DIAGNOSTICS_ROTATION_MISSING_RAINFALL_TOOLTIP_HEADING =
  'Enter rainfall to calculate this for:';
export const FARM_DIAGNOSTICS_ROTATION_TREND_RANGE_TOO_SHORT_TOOLTIP =
  'Trend needs at least 2 years in the selected range to compare an earlier and later half — select a wider range (e.g. Last 3 years) to see one.';
export const FARM_DIAGNOSTICS_ROTATION_TREND_INSUFFICIENT_DATA_TOOLTIP =
  'Not enough data within the selected range for a trend — check the excluded-years note for this row for exactly which years and why.';

// Rotation Efficiency — row-highlight legend
export const FARM_DIAGNOSTICS_ROTATION_LEGEND_TITLE = 'Row highlighting';
// Top / bottom third marks on the WUE, GM and Financial WUE cells (OP-2490).
export const FARM_DIAGNOSTICS_ROTATION_STANDING_TOP_LABEL = 'Top third';
export const FARM_DIAGNOSTICS_ROTATION_STANDING_BOTTOM_LABEL = 'Bottom third';
export const FARM_DIAGNOSTICS_ROTATION_STANDING_NOTE =
  'each column compared across the paddocks shown';
export const FARM_DIAGNOSTICS_ROTATION_WUE_CROP_NOTE =
  'WUE (kg/mm) favours high-yielding crops; GM and Financial WUE compare across crops.';
export const FARM_DIAGNOSTICS_ROTATION_LEGEND_TOP_PERFORMER_LABEL =
  'Top performer (highest WUE)';
export const FARM_DIAGNOSTICS_ROTATION_LEGEND_BELOW_AVERAGE_LABEL =
  'Below average WUE';
export const FARM_DIAGNOSTICS_ROTATION_LEGEND_DECLINING_LABEL =
  'Declining trend';
export const FARM_DIAGNOSTICS_ROTATION_LEGEND_NEUTRAL_LABEL =
  'No highlight — average performer, or not enough data to rank';

export const FARM_DIAGNOSTICS_ROTATION_VIEW_TABLE_LABEL = 'Table';
export const FARM_DIAGNOSTICS_ROTATION_VIEW_CHART_LABEL = 'Chart';
export const FARM_DIAGNOSTICS_ROTATION_CHART_Y_AXIS_LABEL = 'WUE (kg/mm avg)';
export const FARM_DIAGNOSTICS_ROTATION_CHART_NO_PRACTICE_LABEL =
  'No practice tagged';
export const FARM_DIAGNOSTICS_ROTATION_CHART_EXCLUDED_SUFFIX =
  'paddock(s) not shown — no WUE available (see table for details)';
export const FARM_DIAGNOSTICS_ROTATION_CHART_NO_DATA_MESSAGE =
  'No paddock in this scope has a complete WUE for the selected range yet';

// Analysis lens — price scenarios
export const ANALYSIS_PRICE_PESSIMISTIC = 'pessimistic' as const;
export const ANALYSIS_PRICE_EXPECTED = 'expected' as const;
export const ANALYSIS_PRICE_OPTIMISTIC = 'optimistic' as const;

// Analysis lens — production scenarios
export const ANALYSIS_PRODUCTION_LOW = 'low' as const;
export const ANALYSIS_PRODUCTION_EXPECTED = 'expected' as const;
export const ANALYSIS_PRODUCTION_POTENTIAL = 'potential' as const;

// Analysis lens — summary aggregation
export const ANALYSIS_SUMMARY_TOTAL = 'total' as const;
export const ANALYSIS_SUMMARY_AVERAGE = 'average' as const;

// Analysis lens — group-by options
export const ANALYSIS_GROUP_BY_NONE = 'none' as const;
export const ANALYSIS_GROUP_BY_FARM = 'farm' as const;
export const ANALYSIS_GROUP_BY_CROP_VARIETY = 'cropVariety' as const;

// Cashflow lens
export const INPUT_DOC_LENS_CASHFLOW = 'cashflow' as const;

// Nutrition lens (OP-2508)
export const INPUT_DOC_LENS_NUTRITION = 'nutrition' as const;
export const NUTRITION_FARM_VIEW_BALANCE = 'balance' as const;
export const NUTRITION_FARM_VIEW_SEASONS = 'seasons' as const;
// Season range — the Farm diagnostics time-range values, plus this year.
export const NUTRITION_SEASON_RANGE_THIS_YEAR = 'thisYear' as const;

// Insights — lens labels
export const INPUT_DOC_LENS_LABELS: Record<
  | typeof INPUT_DOC_LENS_SUMMARY
  | typeof INPUT_DOC_LENS_SUMMARY_2
  | typeof INPUT_DOC_LENS_ANALYSIS
  | typeof INPUT_DOC_LENS_MARGIN_BREAK_EVEN
  | typeof INPUT_DOC_LENS_CASHFLOW
  | typeof INPUT_DOC_LENS_NUTRITION,
  string
> = {
  // Hidden lens (OP-2364) — kept only so this Record stays exhaustive.
  [INPUT_DOC_LENS_SUMMARY]: 'Summary (legacy)',
  [INPUT_DOC_LENS_SUMMARY_2]: 'Summary',
  [INPUT_DOC_LENS_ANALYSIS]: 'Analysis',
  [INPUT_DOC_LENS_MARGIN_BREAK_EVEN]: 'Margin & break-even',
  [INPUT_DOC_LENS_CASHFLOW]: 'Cashflow',
  [INPUT_DOC_LENS_NUTRITION]: 'Nutrition',
};

// Insights — Nutrition lens labels (OP-2508)
export const NUTRITION_SEASONS_LABEL = 'Years';
export const NUTRITION_SEASON_RANGE_THIS_YEAR_LABEL = 'This year';
export const NUTRITION_DATA_BASIS_LABEL = 'Data basis';
export const NUTRITION_NUTRIENTS_LABEL = 'Nutrients';
export const NUTRITION_NUTRIENT_LABEL = 'Nutrient';
export const NUTRITION_FARM_VIEW_LABELS: Record<string, string> = {
  [NUTRITION_FARM_VIEW_BALANCE]: 'Balance by nutrient',
  [NUTRITION_FARM_VIEW_SEASONS]: 'One nutrient by year',
};
export const NUTRITION_UNIT_KG_PER_HA = 'kg/ha';
export const NUTRITION_COLUMN_PADDOCK = 'Paddock';
export const NUTRITION_COLUMN_AREA = 'Area (ha)';
export const NUTRITION_COLUMN_SEASON = 'Year';
export const NUTRITION_COLUMN_PRODUCT = 'Product';
export const NUTRITION_COLUMN_RATE = 'Rate';
export const NUTRITION_COLUMN_DATE = 'Date';
export const NUTRITION_COLUMN_TOTAL = 'Total';
export const NUTRITION_ROW_APPLIED = 'Applied';
export const NUTRITION_ROW_REMOVED = 'Removed';
export const NUTRITION_ROW_BALANCE = 'Balance';
export const NUTRITION_ROW_TOTAL_APPLIED = 'Total applied';
export const NUTRITION_ROW_TOTAL_REMOVED = 'Total removed';
export const NUTRITION_ROW_TOTAL_BALANCE = 'Balance';
export const NUTRITION_ROW_FARM_TOTAL = 'Area-weighted total';
export const NUTRITION_ROW_FARM_TOTAL_FOR = (farmName: string) =>
  `${farmName} area-weighted total`;
export const NUTRITION_STATUS_BUILDING = 'Building';
export const NUTRITION_STATUS_DECLINING = 'Declining';
export const NUTRITION_STATUS_BALANCED = 'Balanced';
export const NUTRITION_STATUS_NO_REMOVAL_DATA = 'No removal data';
export const NUTRITION_EXPAND_PRODUCTS_LABEL = (paddockName: string) =>
  `Show products for ${paddockName}`;
export const NUTRITION_COLLAPSE_PRODUCTS_LABEL = (paddockName: string) =>
  `Hide products for ${paddockName}`;
export const NUTRITION_PADDOCK_SECTION_TITLE = 'Nutrient audit';
export const NUTRITION_FARM_SECTION_TITLE = 'Nutrient balance by paddock';
export const NUTRITION_CHART_APPLIED_VS_REMOVED_TITLE = 'Applied vs removed';
export const NUTRITION_CHART_RUNNING_BALANCE_TITLE = 'Running balance';
export const NUTRITION_CHART_AREA_BY_STATUS_TITLE = 'Area building vs declining';
export const NUTRITION_CHART_AREA_UNIT = 'ha';
export const NUTRITION_NO_CROP_LABEL = 'No crop';
// Yield scenario (Plan mode) — the production's low / expected / high yields.
export const NUTRITION_YIELD_SCENARIO_LABEL = 'Yield';
export const NUTRITION_YIELD_SCENARIO_LABELS: Record<string, string> = {
  [ANALYSIS_PRODUCTION_LOW]: 'Low',
  [ANALYSIS_PRODUCTION_EXPECTED]: 'Expected',
  [ANALYSIS_PRODUCTION_POTENTIAL]: 'High',
};
/** How a year's yield is labelled — which yield the removal is worked out from. */
export const NUTRITION_YIELD_LABELS: Record<string, string> = {
  [ANALYSIS_PRODUCTION_LOW]: 'Low yield',
  [ANALYSIS_PRODUCTION_EXPECTED]: 'Expected yield',
  [ANALYSIS_PRODUCTION_POTENTIAL]: 'High yield',
};
export const NUTRITION_ACTUAL_YIELD_LABEL = 'Actual yield';
export const NUTRITION_ANALYSIS_EMPTY_LABEL = 'No nutrient analysis recorded';
export const NUTRITION_PART_AREA_TOOLTIP = (applied: string, plan: string) => ({
  title: `Applied to ${applied} of ${plan} ha`,
  lines: [
    `Its nutrients are averaged over the plan’s ${plan} ha, so they count in proportion.`,
  ],
});
export const NUTRITION_AREA_VARIES_TITLE = 'Area by year';
export const NUTRITION_AREA_VARIES_NOTE = 'The latest year is shown.';
export const NUTRITION_YEAR_ITEM = (year: number, value: string) =>
  `${year} · ${value}`;
export const NUTRITION_SHOW_LABEL = 'Show';
export const NUTRITION_SHOW_CROP_DETAILS_LABEL = 'Area and yield';
/** Hover on the +applied −removed line under each balance in the farm grid. */
export const NUTRITION_APPLIED_REMOVED_TOOLTIP = (
  applied: string,
  removed: string
) => ({
  lines: [`Applied: ${applied} kg/ha`, `Removed by the crop: ${removed} kg/ha`],
});
export const NUTRITION_AREA_COLUMN_HELP = {
  title: 'Area',
  lines: [
    'From each plan, not the paddock.',
    'Shows the latest year in range.',
    'Underlined when it changes from year to year.',
  ],
};
export const NUTRITION_ROW_CROP_REMOVAL = 'Crop removal';
export const NUTRITION_ROTATION_TITLE = 'Crops by year';
export const NUTRITION_AREA_UNIT_HA = 'ha';
export const NUTRITION_CHART_SEASON_HOVER_HINT =
  'Hover over a year to see its figures.';
export const NUTRITION_CHART_AREA_HOVER_HINT =
  'Hover over a nutrient to see its areas.';
export const NUTRITION_CHART_PANEL_DEFAULT_WIDTH_REM = 32;
export const NUTRITION_CHART_PANEL_MIN_WIDTH_REM = 22;
export const NUTRITION_CHART_PANEL_MAX_WIDTH_REM = 64;
export const NUTRITION_CHART_PANEL_KEY_STEP_REM = 2;
export const SAVING_STATUS_SAVING_LABEL = 'Saving…';
export const NUTRITION_PADDOCK_YEAR_LABEL = (paddock: string, year: number) =>
  `${paddock}, ${year}`;
export const NUTRITION_FAILED_SAVE_DESCRIPTION = (
  product: string,
  location: string
) => `${product} on ${location}`;
export const NUTRITION_SAVED_TOOLTIP =
  'Changes save to each plan straight away.';
export const NUTRITION_CHARTS_OPEN_STORAGE_KEY = 'nutrition-charts-open';
export const NUTRITION_CHARTS_LABEL = 'Charts';
export const NUTRITION_CHARTS_HIDE_LABEL = 'Hide charts';
export const NUTRITION_CHARTS_SHOW_LABEL = 'Show charts';
export const NUTRITION_CHART_WIDTH_STORAGE_KEY = 'nutrition-chart-panel-width';
export const NUTRITION_CHART_PANEL_RESIZE_LABEL = 'Resize the charts panel';
export const NUTRITION_COLUMN_PRODUCTS = 'Products';
export const NUTRITION_EXPAND_ALL_LABEL = 'Expand all';
export const NUTRITION_COLLAPSE_ALL_LABEL = 'Collapse all';
export const NUTRITION_PRODUCT_COUNT_LABEL = (count: number) =>
  count === 1 ? '1 product' : `${count} products`;
export const NUTRITION_SEASON_NO_PRODUCTS_LABEL = 'No fertiliser this year';
export const NUTRITION_EDIT_RATE_LABEL = (productName: string) =>
  `Rate for ${productName}`;


// Insights — Margin & break-even lens placeholders
export const GROSS_MARGIN_INVALID_SCOPE_MESSAGE =
  'Select a paddock, plan or crop in the farm tree to see margin and break-even.';
export const GROSS_MARGIN_NO_DATA_MESSAGE =
  'No data available to view margin and break-even';

// Insights — Summary / Analysis / Cashflow lens empty states
export const SUMMARY_NO_DATA_MESSAGE = 'No summary data';
export const ANALYSIS_NO_DATA_MESSAGE = 'No analysis data';
export const CASHFLOW_NO_DATA_MESSAGE = 'No cashflow data';

export const CASHFLOW_PERIOD_MONTH = 'month' as const;
export const CASHFLOW_PERIOD_QUARTER = 'quarter' as const;

export const CASHFLOW_YEAR_CALENDAR = 'calendar' as const;
export const CASHFLOW_YEAR_FINANCIAL = 'financial' as const;

// 'categories' shows category/crop totals only; 'products' shows individual
// product/crop line items instead, with no category subtotal rows.
export const CASHFLOW_DETAIL_CATEGORIES = 'categories' as const;
export const CASHFLOW_DETAIL_PRODUCTS = 'products' as const;

// AU financial year — hardcoded (no org-level FY setting exists yet).
export const FINANCIAL_YEAR_START_MONTH = 6; // July, 0-indexed

export const CASHFLOW_NO_DATE_LABEL = 'No Date';

// Analysis lens — column labels
export const ANALYSIS_COLUMN_LABELS: Record<string, string> = {
  farmName: 'Farm Name',
  areaHa: 'Area',
  crop: 'Crop',
  variety: 'Variety',
  startDate: 'Start Date',
  price: 'Price',
  yield: 'Yield',
  production: 'Prod.',
  income: 'Income',
  totalCosts: 'Tot. Costs',
  grossMargin: 'Gross Margin',
  costPercentIncome: 'Cost % Income',
};

// Summary lens — Entity Category
export const SUMMARY_ENTITY_ALL = 'all' as const;
export const SUMMARY_ENTITY_SEED = 'seed' as const;
export const SUMMARY_ENTITY_SOIL_INPUT = 'soilInput' as const;
export const SUMMARY_ENTITY_PLANT_PROTECTION = 'plantProtection' as const;
export const SUMMARY_ENTITY_SEED_DRESS = 'seedDress' as const;
export const SUMMARY_ENTITY_BIOSTIMULANT = 'biostimulant' as const;
export const SUMMARY_ENTITY_OPERATION = 'operation' as const;
export const SUMMARY_ENTITY_PRODUCTION = 'production' as const;

export const SUMMARY_ENTITY_LABELS: Record<string, string> = {
  [SUMMARY_ENTITY_ALL]: 'Cost vs income',
  [SUMMARY_ENTITY_SEED]: 'Seed',
  [SUMMARY_ENTITY_SOIL_INPUT]: 'Soil Input',
  [SUMMARY_ENTITY_PLANT_PROTECTION]: 'Plant Protection',
  [SUMMARY_ENTITY_SEED_DRESS]: 'Seed Treatment',
  [SUMMARY_ENTITY_BIOSTIMULANT]: 'Biostimulant',
  [SUMMARY_ENTITY_OPERATION]: 'Operation',
  [SUMMARY_ENTITY_PRODUCTION]: 'Production',
};

// Summary lens — Data Metric
export const SUMMARY_METRIC_QUANTITY = 'quantity' as const;
export const SUMMARY_METRIC_TOTAL_AREA = 'totalArea' as const;
export const SUMMARY_METRIC_TOTAL_COST = 'totalCost' as const;
export const SUMMARY_METRIC_COST_PER_HA = 'costPerHa' as const;

// Summary lens — Temporal Layout
export const SUMMARY_TEMPORAL_FLAT = 'flatTotal' as const;
export const SUMMARY_TEMPORAL_MONTHLY = 'monthlyBreakdown' as const;

// Plan/Actual toggle labels (PlanActualSegmented)
export const PLAN_ACTUAL_PLAN_LABEL = 'Plan';
export const PLAN_ACTUAL_ACTUAL_LABEL = 'Actual';

// Summary lens — Data basis (which task category the Summary reads)
export const SUMMARY_DATA_BASIS_PLAN = 'plan' as const;
export const SUMMARY_DATA_BASIS_ACTUAL = 'actual' as const;
export const SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL = 'planVsActual' as const;

export const SUMMARY_DATA_BASIS_LABELS: Record<string, string> = {
  [SUMMARY_DATA_BASIS_PLAN]: 'Plan',
  [SUMMARY_DATA_BASIS_ACTUAL]: 'Actual',
  [SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL]: 'Plan vs Actual',
};

// Static labels for Summary sections the data-basis selector doesn't apply to
export const SUMMARY_DATA_BASIS_AREA_NOTE =
  'Paddock area — the same for Plan and Actual';
export const SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL_NOTE =
  'Always shows Plan and Actual side by side';
export const SUMMARY_DATA_BASIS_NO_DATA_MESSAGE =
  'No input costs recorded for this data basis in the current scope. Add them in Planning, or choose another data basis above.';

// Summary lens — Input summaries view (OP-2394): each section shows its table or its chart, never both
export const SUMMARY_INPUT_VIEW_TABLE = 'table' as const;
export const SUMMARY_INPUT_VIEW_CHART = 'chart' as const;
export const SUMMARY_INPUT_VIEW_STORAGE_KEY = 'insights-summary-input-view';
export const SUMMARY_CHART_AREA_NOT_CHARTED_MESSAGE =
  'Area isn’t charted per product. Choose Quantity, Total cost or $/ha to see the chart.';
export const SUMMARY_CHART_NO_DATA_MESSAGE =
  'Nothing to chart yet. Add a rate and cost to this section’s items in Planning, or choose another data basis above.';

// Summary lens — Cost by Crop (OP-2433)
export const COST_BY_CROP_PLAN_VS_ACTUAL_NOTE =
  'Spend mix per crop. For the Plan vs Actual dollar comparison, see';
export const COST_BY_CROP_NO_AREA_TOOLTIP =
  'No area recorded for this crop — $/ha can’t be calculated';

// Summary lens — Spend over time (OP-2389)
export const SUMMARY_SPEND_OVER_TIME_SECTION_ID = 'summary2-spendOverTime';
export const SUMMARY_PLAN_VS_ACTUAL_SECTION_ID = 'summary2-planVsActual';

// Input step type labels
export const INPUT_STEP_LABEL_APPLICATION = 'Operation';
export const INPUT_STEP_LABEL_CROP_PROTECTION = 'Plant Protection';
export const INPUT_STEP_LABEL_ADJUVANT = 'Adjuvant';
export const INPUT_STEP_LABEL_SOIL_INPUT = 'Soil Input';
export const INPUT_STEP_LABEL_BIOSTIMULANT = 'Biostimulant';
export const INPUT_STEP_LABEL_SEED = 'Seed';
export const INPUT_STEP_LABEL_SEED_DRESS = 'Seed Treatment';

// Input step group keys
export const OPERATION_STEP_GROUP_KEY = 'operation';
export const PLANTPROTECTION_STEP_GROUP_KEY = 'plantProtection';
export const ADJUVANT_STEP_GROUP_KEY = 'adjuvant';
export const SOILINPUT_STEP_GROUP_KEY = 'soilInput';
export const BIOSTIMULANT_STEP_GROUP_KEY = 'biostimulant';
export const SEED_STEP_GROUP_KEY = 'seed';
export const SEEDDRESS_STEP_GROUP_KEY = 'seedDress';
export const PRODUCTION_STEP_GROUP_KEY = 'production';

// Step Input Selector — UI copy
export const STEP_SELECTOR_SEARCH_PLACEHOLDER = 'Search...';
export const STEP_SELECTOR_NO_STEP_TYPES_MESSAGE =
  'No step types available for this task.';
export const STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE = 'No options found';
export const STEP_SELECTOR_CANCEL_LABEL = 'Cancel';
export const STEP_SELECTOR_SAVE_LABEL = 'Save';
export const STEP_SELECTOR_MULTI_SELECT_HINT =
  'Hold Ctrl / \u2318 to select multiple items.';
export const STEP_SELECTOR_SEED_DECIDE_LATER_LABEL = 'Pick Variety Later';
export const STEP_SELECTOR_SEED_CLEAR_VARIETY_LABEL = 'Clear variety';
export const STEP_SELECTOR_SEED_CHANGE_VARIETY_LABEL = 'Change Variety';
export const STEP_SELECTOR_SEED_TREATMENT_CHANGE_DETAILS_LABEL =
  'Change Details';
export const STEP_SELECTOR_FETCHING_LABEL = 'Fetching latest results\u2026';

// Seed Treatment -> target seed picker (POC)
export const SEED_TARGET_PICKER_TITLE = 'Seed treated';
export const SEED_TARGET_EMPTY_MESSAGE = 'Add a Seed input to this task first.';
export const CHANGE_SEED_TARGET_MENU_LABEL = 'Change seed to be treated';
export const DUPLICATE_TASK_MENU_LABEL = 'Duplicate';
export const SEED_TARGET_MISSING_TOOLTIP =
  'Seed to be treated not set — click to choose one';
export const SEED_TREATMENT_RATE_MODE_PER_HA_LABEL = '/ha';
export const SEED_TREATMENT_RATE_MODE_PER_100KG_LABEL = '/100kg';
export const SEED_TREATMENT_RATE_LABEL = 'Rate';
export const SEED_TREATMENT_RATE_HINT_PER_HA = 'unit/ha';
export const SEED_TREATMENT_RATE_HINT_PER_100KG = 'unit/100kg';
export const SEED_TREATMENT_UNIT_LABEL = 'Unit';
export const SEED_TREATMENT_COST_LABEL = 'Cost';
export const SEED_TREATMENT_COST_HINT = '/unit';
export const SEED_TREATMENT_ADD_LABEL = 'Add';
export const STEP_SELECTOR_SAVE_BLOCKED_BY_SEED_TREATMENT_TOOLTIP =
  'Add your seed treatment changes, or close the details, to save.';
export const SEED_VARIETY_FLYOUT_CLOSE_LABEL = 'Close variety list';
export const SEED_TREATMENT_FLYOUT_CLOSE_LABEL = 'Close seed treatment details';
export const SEED_TREATMENT_APPLY_LABEL = 'Product Applied:';
export const SEED_TWO_PANEL_BACK_TO_CROPS_TOOLTIP =
  'Click to go back to crop selection';
export const SEED_TWO_PANEL_CROP_LABEL = 'Crop';
export const SEED_TWO_PANEL_VARIETY_LABEL = 'Variety';
export const SEED_TWO_PANEL_SEED_STRIP_TOOLTIP =
  'Click to choose a different seed';
export const SEED_TWO_PANEL_VARIETY_STRIP_TOOLTIP =
  'Select a seed first to see its varieties';
export const SEED_TREATMENT_TWO_PANEL_BACK_TOOLTIP =
  'Click to go back to product selection';
export const SEED_TREATMENT_TWO_PANEL_PRODUCT_LABEL = 'Product';
export const SEED_TREATMENT_TWO_PANEL_DETAILS_LABEL = 'Details';
export const SEED_TREATMENT_TWO_PANEL_PRODUCT_STRIP_TOOLTIP =
  'Click to choose a different product';
export const SEED_TREATMENT_TWO_PANEL_DETAILS_STRIP_TOOLTIP =
  'Select a product first to see its details';
export const SEED_NO_VARIETY_LABEL = 'No Variety';

// Paddock Plan Preview — category labels (OP-2227)
export const PADDOCK_PLAN_PREVIEW_CATEGORY_LABEL_CROP_VARIETY = 'Crop/Variety';
export const PADDOCK_PLAN_PREVIEW_CATEGORY_LABEL_SOIL_INPUTS = 'Soil inputs';
export const PADDOCK_PLAN_PREVIEW_CATEGORY_LABEL_PLANT_PROTECTION =
  'Plant Protection';
export const PADDOCK_PLAN_PREVIEW_CATEGORY_LABEL_BIOSTIMULANTS =
  'Biostimulants';
export const PADDOCK_PLAN_PREVIEW_CATEGORY_LABEL_OPERATIONS = 'Operations';
export const PADDOCK_PLAN_PREVIEW_CATEGORY_LABEL_PRODUCTION = 'Production';

export const PADDOCK_PLAN_PREVIEW_ERROR_MESSAGE_GENERIC =
  'Something went wrong loading this plan. Please try again.';
export const PADDOCK_PLAN_PREVIEW_NOT_FOUND_MESSAGE = 'Plan not found.';

// Farm Plan Preview — every paddock in the organisation for a plan year (OP-2380)
export const FARM_PLAN_PREVIEW_NO_PLAN_MESSAGE = 'No plan';
export const FARM_PLAN_PREVIEW_NO_ACTUALS_MESSAGE = 'No actuals recorded';
export const FARM_PLAN_PREVIEW_EMPTY_MESSAGE = 'No paddock plans to preview.';
// Chrome uses document.title as the suggested print / save-as-PDF filename,
// so it names the version too — a saved Actuals PDF must not read as a plan.
export const buildFarmPlanPreviewDocumentTitle = (
  organisationName: string,
  yearLabel: string,
  basisHeading: string
): string => `${organisationName} — ${basisHeading} ${yearLabel}`;
export const buildPaddockPlanPreviewDocumentTitle = (
  paddockName: string,
  basisHeading: string,
  year: number | null
): string =>
  `${paddockName} — ${basisHeading}${year !== null ? ` ${year}` : ''}`;
export const FARM_PLAN_PREVIEW_ERROR_MESSAGE_GENERIC =
  'Something went wrong loading these plans. Please try again.';
export const FARM_PLAN_PREVIEW_SECTION_ERROR_MESSAGE =
  'This paddock plan could not be displayed.';
// A plan whose paddock link matches nothing in the organisation would
// otherwise be invisible in a report built by walking paddocks (OP-2171 AC6).
export const FARM_PLAN_PREVIEW_OTHER_PLANS_HEADING = 'Other plans';
export const FARM_PLAN_PREVIEW_OTHER_PLANS_HINT =
  'These plans are not linked to a paddock in this organisation.';
export const FARM_PLAN_PREVIEW_UNNAMED_PLAN_LABEL = 'Unnamed plan';

// Plan preview — Insights and Farm diagnostics sections (OP-2490)
export const PLAN_PREVIEW_SECTIONS_STORAGE_KEY = 'plan-preview-sections';
export const PLAN_PREVIEW_DIAGNOSTICS_SNAPSHOT_STORAGE_KEY =
  'plan-preview-diagnostics';
export const PLAN_PREVIEW_SECTION_CROP_AREA = 'cropArea';
export const PLAN_PREVIEW_SECTION_COST_BY_CROP = 'costByCrop';
export const PLAN_PREVIEW_SECTION_INPUT_SUMMARY = 'inputSummary';
export const PLAN_PREVIEW_SECTION_ROTATION_EFFICIENCY = 'rotationEfficiency';
export const PLAN_PREVIEW_SECTION_ROTATION_SUMMARY = 'rotationSummary';
export const PLAN_PREVIEW_SECTION_ROTATION_HISTORY = 'rotationHistory';
export const PLAN_PREVIEW_SECTION_PADDOCK_PLAN = 'paddockPlan';
export const PLAN_PREVIEW_SECTION_MARGIN_BREAK_EVEN = 'marginBreakEven';
export const PLAN_PREVIEW_SECTION_NUTRITION_TALLY = 'nutritionTally';
export const PLAN_PREVIEW_SECTION_WUE_TREND = 'wueTrend';
export const PLAN_PREVIEW_SECTION_FINANCIAL_EFFICIENCY = 'financialEfficiency';
export const PLAN_PREVIEW_SECTION_BIO_VS_FINANCIAL = 'bioVsFinancial';
export const PLAN_PREVIEW_SECTION_LABELS = {
  [PLAN_PREVIEW_SECTION_CROP_AREA]: 'Crop area',
  [PLAN_PREVIEW_SECTION_COST_BY_CROP]: 'Cost by crop',
  [PLAN_PREVIEW_SECTION_INPUT_SUMMARY]: 'Input summary',
  [PLAN_PREVIEW_SECTION_ROTATION_EFFICIENCY]: 'Rotation efficiency',
  [PLAN_PREVIEW_SECTION_ROTATION_SUMMARY]: 'Rotation summary',
  [PLAN_PREVIEW_SECTION_ROTATION_HISTORY]: 'Rotation history',
  [PLAN_PREVIEW_SECTION_PADDOCK_PLAN]: 'Paddock plan',
  [PLAN_PREVIEW_SECTION_MARGIN_BREAK_EVEN]: 'Margin & break-even',
  [PLAN_PREVIEW_SECTION_NUTRITION_TALLY]: 'Nutrition tally',
  [PLAN_PREVIEW_SECTION_WUE_TREND]: 'WUE trend',
  [PLAN_PREVIEW_SECTION_FINANCIAL_EFFICIENCY]: 'Financial efficiency',
  [PLAN_PREVIEW_SECTION_BIO_VS_FINANCIAL]: 'Bio vs Financial',
} as const;
export const PLAN_PREVIEW_SETTINGS_BUTTON_LABEL = 'What to include';
export const PLAN_PREVIEW_SETTINGS_ORG_GROUP_LABEL = 'Organisation summary';
export const PLAN_PREVIEW_SETTINGS_PADDOCK_GROUP_LABEL = 'Each paddock';
export const PLAN_PREVIEW_PRINT_BUTTON_LABEL = 'Print';
export const PLAN_PREVIEW_NAV_LABEL = 'Contents';
export const PLAN_PREVIEW_ORG_SUMMARY_HEADING = 'Organisation summary';
export const PLAN_PREVIEW_SECTION_CHIPS_LABEL = 'Paddock sections';
export const PLAN_PREVIEW_NUTRIENT_COLUMN_LABEL = 'Nutrient';
export const PLAN_PREVIEW_NUTRIENT_RATE_COLUMN_LABEL = 'Planned rate';
export const PLAN_PREVIEW_NUTRIENT_APPLIED_RATE_COLUMN_LABEL = 'Applied rate';
export const PLAN_PREVIEW_NUTRITION_BASIS_NOTE =
  'Planned soil inputs, averaged over the paddock area.';
export const PLAN_PREVIEW_NUTRITION_ACTUAL_BASIS_NOTE =
  'Applied soil inputs, averaged over the paddock area.';
// The preview's version — what every plan-year section reads (OP-2490).
export const PLAN_PREVIEW_BASIS_SWITCH_LABEL = 'Version';
export const PLAN_PREVIEW_BASIS_OPTION_PLAN = 'Plan';
export const PLAN_PREVIEW_BASIS_OPTION_ACTUAL = 'Actuals';
// Printed in the heading and the PDF filename, so the version is never ambiguous.
export const PLAN_PREVIEW_BASIS_HEADING_PLAN = 'Plan';
export const PLAN_PREVIEW_BASIS_HEADING_ACTUAL = 'Actuals to date';
export const PLAN_PREVIEW_SECTION_PADDOCK_ACTUALS_LABEL = 'Paddock actuals';
export const PLAN_PREVIEW_COPY_LINK_LABEL = 'Copy link';
export const PLAN_PREVIEW_LINK_COPIED_LABEL = 'Link copied';
export const PLAN_PREVIEW_COPY_LINK_HINT =
  'Opens this preview with the same version and sections. Recipients need access to this organisation.';
// Trends are a record of past seasons, so they read Actual where it exists —
// said on the page so a grower doesn't take them for planned figures.
export const PLAN_PREVIEW_HISTORY_BASIS_NOTE =
  'Past years use Actual figures where recorded, otherwise Plan.';
// The preview has no time-range control, so it says which years are averaged.
export const buildPlanPreviewRotationWindowNote = (
  startYear: number,
  endYear: number
): string =>
  startYear === endYear
    ? `Averages for ${startYear}.`
    : `Averages over ${startYear}–${endYear}.`;
// Seasons the rotation summary and each paddock's rotation strip cover —
// centred on the preview's year, so history before it and plans after it.
// Rotation efficiency averages the same number of seasons ending at the
// preview year (OP-2490).
export const PLAN_PREVIEW_ROTATION_YEARS_OPTIONS = [3, 5] as const;
export const PLAN_PREVIEW_ROTATION_YEARS_DEFAULT = 5;
export const PLAN_PREVIEW_ROTATION_YEARS_LABEL = 'Rotation years';
export const buildRotationYearsOptionLabel = (count: number): string =>
  `${count} years`;
export const PLAN_PREVIEW_NAV_GROUP_BY_LABEL = 'Group by';
export const PLAN_PREVIEW_NAV_GROUP_FARM = 'farm';
export const PLAN_PREVIEW_NAV_GROUP_CROP = 'crop';
export const PLAN_PREVIEW_NAV_GROUP_LABELS = {
  [PLAN_PREVIEW_NAV_GROUP_FARM]: 'Farm',
  [PLAN_PREVIEW_NAV_GROUP_CROP]: 'Crop',
} as const;
export const PLAN_PREVIEW_NAV_GROUPING_STORAGE_KEY = 'plan-preview-nav-grouping';
export const PLAN_PREVIEW_NAV_COLLAPSED_STORAGE_KEY = 'plan-preview-nav-collapsed';
export const PLAN_PREVIEW_NAV_COLLAPSE_LABEL = 'Hide contents';
export const PLAN_PREVIEW_NAV_EXPAND_LABEL = 'Show contents';

// Paddock Plan Preview — step table column labels (OP-2229)
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_NAME = 'Name';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_RATE = 'Rate';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_QUANTITY = 'Quantity';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_COST = 'Cost';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_TOTAL_COST = 'Total Cost';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_COST_PER_HA = 'Cost/ha';

// Paddock Plan Preview — Production category column labels
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_YIELD_LOW = 'Yield Low';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_YIELD_EXPECTED =
  'Yield Expected';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_YIELD_HIGH = 'Yield High';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_PRICE_LOW = 'Price Low';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_PRICE_EXPECTED =
  'Price Expected';
export const PADDOCK_PLAN_PREVIEW_COLUMN_LABEL_PRICE_HIGH = 'Price High';

// Right Panel — tab IDs
export const RIGHT_PANEL_TAB_NUTRITION = 'nutrition' as const;
export const RIGHT_PANEL_TAB_FINANCIAL = 'financial' as const;
export const RIGHT_PANEL_TAB_CATEGORY_SPEND = 'categorySpend' as const;
export const RIGHT_PANEL_TAB_QUANTITY = 'quantity' as const;

// Right Panel — canonical display order
export const RIGHT_PANEL_CANONICAL_ORDER = [
  'nutrition',
  'financial',
  'categorySpend',
  'quantity',
] as const;

// Right Panel — full labels (pinned/floating panel headers and tab switcher)
export const RIGHT_PANEL_TAB_LABELS: Record<string, string> = {
  [RIGHT_PANEL_TAB_NUTRITION]: 'Nutrition Tally',
  [RIGHT_PANEL_TAB_FINANCIAL]: 'Financial Risk',
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: 'Category Spend',
  [RIGHT_PANEL_TAB_QUANTITY]: 'Quantity Required',
};

// Right Panel — short labels (collapsed edge strip)
export const RIGHT_PANEL_TAB_SHORT_LABELS: Record<string, string> = {
  [RIGHT_PANEL_TAB_NUTRITION]: 'Nutrition',
  [RIGHT_PANEL_TAB_FINANCIAL]: 'Financial',
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: 'Category',
  [RIGHT_PANEL_TAB_QUANTITY]: 'Quantity',
};

// Right Panel — tools (OP-2383). The tabs at the top of the pinned panel
// switch it between the live totals tabs and a calculator.
export const RIGHT_PANEL_TOOL_LIVE_TOTALS = 'liveTotals' as const;
export const RIGHT_PANEL_TOOL_LIME_MATE = 'limeMate' as const;
export const RIGHT_PANEL_TOOL_LABELS: Record<string, string> = {
  [RIGHT_PANEL_TOOL_LIVE_TOTALS]: 'Live totals',
  [RIGHT_PANEL_TOOL_LIME_MATE]: 'LimeMate',
};

// LimeMate calculator (OP-2383)
export const LIME_MATE_TITLE = 'LimeMate calculator';
export const LIME_MATE_SUBTITLE =
  'Soil pH amendment rate calculator for Australian conditions';
export const LIME_MATE_OPEN_IN_NEW_TAB_LABEL = 'Open in new tab';
// Measured calculator width (px) at which input grids go to two columns.
export const LIME_MATE_TWO_COLUMN_MIN_WIDTH_PX = 320;

// Right Panel — layout / persistence (all widths in rem)
export const RIGHT_PANEL_MIN_WIDTH = 12.5; // rem ≈ 200px at 16px base
export const RIGHT_PANEL_MAX_WIDTH = 50; // rem ≈ 800px at 16px base
export const RIGHT_PANEL_INITIAL_WIDTH = 18; // rem ≈ 288px — matches sidebar default
// Below this measured content width, a panel's Plan/Actual columns collapse
// to one column + a small toggle rather than showing both side by side.
// Set comfortably below the ~256px content width of the panel's own default
// (RIGHT_PANEL_INITIAL_WIDTH, 288px, minus its 1rem/side padding) so the
// default view shows both columns; only a meaningfully narrower resize (or a
// floating tab dragged smaller) falls back to one column.
export const RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX = 220;
export const RIGHT_PANEL_WIDTH_STORAGE_KEY = 'right-panel-width';
export const RIGHT_PANEL_STATE_STORAGE_KEY = 'right-panel-state';

// Right Panel — floating panel geometry (rem; converted to px for react-rnd at runtime)
export const RIGHT_PANEL_FLOAT_DEFAULT_WIDTH = 18; // rem ≈ 288px at 16px base
export const RIGHT_PANEL_FLOAT_DEFAULT_HEIGHT = 25; // rem ≈ 400px at 16px base
export const RIGHT_PANEL_FLOAT_MIN_WIDTH = 12.5; // rem ≈ 200px at 16px base
export const RIGHT_PANEL_FLOAT_MIN_HEIGHT = 10; // rem ≈ 160px at 16px base

// Delete Organisation modal
export const DELETE_ORGANISATION_MENU_ITEM_LABEL = 'Delete';
export const DELETE_ORGANISATION_LABEL = 'Delete Organisation';
export const DELETE_ORGANISATION_LOADING_LABEL = 'Deleting...';
export const DELETE_ORGANISATION_WARNING_PREFIX = "You're about to delete ";
export const DELETE_ORGANISATION_WARNING_SUFFIX =
  '. If you proceed, you and any others who share this organisation will no longer be able to access any data.';
export const DELETE_ORGANISATION_CONFIRM_LABEL =
  'Type "delete" below to confirm';
export const DELETE_ORGANISATION_CONFIRM_PLACEHOLDER = 'Type delete to confirm';
export const DELETE_ORGANISATION_CONFIRM_ERROR_MESSAGE =
  'Type "delete" to confirm';
export const DELETE_ORGANISATION_API_ERROR_MESSAGE =
  'Unable to delete organisation. Please try again.';

// Organisation Create/Edit modal
export const ORGANISATION_NAME_REQUIRED_MESSAGE =
  'Organisation name is required';
export const ORGANISATION_NAME_MAX_LENGTH_MESSAGE = `Organisation name must be ${ORGANISATION_NAME_MAX_LENGTH} characters or fewer`;
export const ORGANISATION_ADDRESS1_REQUIRED_MESSAGE =
  'Address line 1 is required';
export const ORGANISATION_CITY_REQUIRED_MESSAGE = 'City is required';
export const ORGANISATION_STATE_REQUIRED_MESSAGE = 'State is required';
export const ORGANISATION_POSTCODE_REQUIRED_MESSAGE = 'Postcode is required';
export const ORGANISATION_COUNTRY_REQUIRED_MESSAGE = 'Country is required';
export const ORGANISATION_FIRST_NAME_REQUIRED_MESSAGE =
  'First name is required';
export const ORGANISATION_LAST_NAME_REQUIRED_MESSAGE = 'Last name is required';

// Open organisation flow
export const ORGANISATION_LOAD_ERROR_MESSAGE =
  'We couldn’t load this organisation. Please try again';

// Farms page — sidebar
export const SIDEBAR_COLLAPSE_LABEL = 'Collapse sidebar';

// Farm tree — grouping mode view toggle labels
export const TREE_GROUP_BY_VIEW_LABELS: Record<
  typeof TREE_GROUP_BY_FARM | typeof TREE_GROUP_BY_CROP,
  string
> = {
  [TREE_GROUP_BY_FARM]: 'Farm view',
  [TREE_GROUP_BY_CROP]: 'Crop view',
};

// Farm tree — year filter controls
export const TREE_YEAR_ALL_SHORT_LABEL = 'All';
export const TREE_YEAR_PREVIOUS_LABEL = 'Previous year';
export const TREE_YEAR_NEXT_LABEL = 'Next year';
export const TREE_YEAR_FILTER_TOOLTIP_TITLE = 'Year filter';
export const TREE_YEAR_FILTER_ALL_YEARS_TEXT = 'Showing all years';
export const TREE_YEAR_FILTER_ARIA_LABEL = 'Filter plans by year';

// Farm tree — search and expand/collapse controls (OP-2384)
export const TREE_SEARCH_PLACEHOLDER = 'Search paddock, crop or variety';
export const TREE_SEARCH_NO_MATCHES_MESSAGE =
  'No matches. Try a different paddock, crop or variety name';
export const TREE_EXPAND_ALL_LABEL = 'Expand all';
export const TREE_COLLAPSE_ALL_LABEL = 'Collapse all';

// Farm tree — loading/error states
export const FARM_TREE_LOADING_MESSAGE = 'Loading farm structure...';
export const FARM_TREE_ORGANISATIONS_ERROR_MESSAGE =
  "We couldn't load your organizations. Please try again";
export const FARM_TREE_FARM_ERROR_MESSAGE =
  "We couldn't load this farm. Please try again";

// Farm tree — shared "unsaved changes" confirm dialog (OP-2260)
export const UNSAVED_CHANGES_MODAL_TITLE = 'Unsaved Changes';
export const UNSAVED_CHANGES_DISCARD_LABEL = 'Discard changes';
export const UNSAVED_CHANGES_KEEP_EDITING_LABEL = 'Keep editing';
export const UNSAVED_CHANGES_LEAVE_INPUT_MESSAGE =
  'You have unsaved changes. Do you want to leave this input?';
export const UNSAVED_CHANGES_LEAVE_PAGE_MESSAGE =
  'You have unsaved changes. Do you want to leave this page?';

// Farm tree — delete plan confirm dialog
export const DELETE_PLAN_LABEL = 'Delete';
export const DELETE_PLAN_MODAL_TITLE = 'Delete plan?';
export const DELETE_PLAN_CANCEL_LABEL = 'Cancel';
export const DELETE_PLAN_BODY_SUFFIX =
  'will be removed from the tree. This cannot be undone from here.';
export const DELETE_PLAN_API_ERROR_MESSAGE =
  'Unable to delete paddock plan. Please try again.';

// Farm tree — node labels
export const UNNAMED_INPUT_LABEL = 'Unnamed Input';
export const ADD_PLAN_BUTTON_LABEL = 'Add plan';
export const OTHER_INPUTS_NODE_LABEL = 'Other Inputs';

// Organisation workspace — saved-by fallback when no name is resolvable
export const SAVED_BY_UNKNOWN_LABEL = 'Unknown';

// Organisation workspace — breadcrumb / ID copy
export const ORG_ID_LABEL = 'Org ID';
export const FARM_ID_LABEL = 'Farm ID';
export const PADDOCK_ID_LABEL = 'Paddock ID';
export const INPUT_ID_LABEL = 'Input ID';
export const COPY_ID_ACTION_PREFIX = 'Copy';
export const COPY_ID_COPIED_LABEL = 'Copied';
export const RETURN_TO_ORGANISATION_LIST_LABEL = 'Return to Organisation list';
export const PADDOCK_NAME_FALLBACK = 'Paddock';

// Organisation workspace — plan preview / tab switcher
export const PADDOCK_PLAN_PREVIEW_POPUP_BLOCKED_MESSAGE =
  'Please allow pop-ups to preview this plan.';
export const PREVIEW_BUTTON_LABEL = 'Preview';
export const FARM_PLAN_PREVIEW_POPUP_BLOCKED_MESSAGE =
  'Please allow pop-ups to preview these plans.';
export const PREVIEW_MENU_THIS_PADDOCK_LABEL = 'This paddock';
export const PREVIEW_MENU_ALL_PADDOCKS_LABEL = 'All paddocks';
export const INPUT_WORKSPACE_PLACEHOLDER_MESSAGE =
  'Select a paddock or plan to view details';
export const NO_PLAN_AVAILABLE_MESSAGE =
  'No plan available. Create a plan to get started.';
export const CHOOSE_PLAN_PROMPT_MESSAGE =
  'This paddock has multiple plans. Select one to view:';

// Input workspace — tasks grid, duplicate step records
// Step ids are unique by contract, but legacy and seeded documents repeat a
// placeholder GUID. Every step action resolves a row by that id, so the grid
// withdraws them rather than act on the wrong row. Both messages describe the
// same condition and are kept together so they cannot drift apart.
export const DUPLICATE_STEP_IDS_MESSAGE =
  'Re-ordering, editing and deleting are unavailable: two or more inputs in this task share the same record. Please contact support.';
export const DUPLICATE_STEP_IDS_DELETE_REASON =
  'Unavailable: this input shares a record with another in the task. Please contact support.';

// Tasks grid (Workspace/Tasks/TasksTableGrid.tsx) — task-level column headers
export const TASKS_GRID_COLUMN_LABEL_TASK = 'Task';
export const TASKS_GRID_COLUMN_LABEL_DATE = 'Date';
export const TASKS_GRID_COLUMN_LABEL_TIMING = 'Timing';
export const TASKS_GRID_COLUMN_LABEL_AREA = 'Area';
export const TASKS_GRID_COLUMN_LABEL_AREA_UNIT = 'Area unit';
export const TASKS_GRID_COLUMN_LABEL_COST_PER_HA = '$/ha';

// Tasks grid — step sub-table column headers
export const TASKS_GRID_COLUMN_LABEL_INPUT_NAME = 'Input name';
export const TASKS_GRID_COLUMN_LABEL_RATE = 'Rate';
export const TASKS_GRID_COLUMN_LABEL_RATE_UNIT = 'Rate unit';
export const TASKS_GRID_COLUMN_LABEL_COST_PER_UNIT = 'Cost /unit';
export const TASKS_GRID_COLUMN_LABEL_REQUIRED = 'Required';

// Tasks grid — add task/input buttons and shortcuts
export const TASKS_GRID_ADD_BUTTON_LABEL = 'Add';

// Tasks grid — quick-add row and new-task entry (OP-2534)
export const TASKS_GRID_MOA_CAPTION_PREFIX = 'MoA';
export const TASKS_GRID_NUTRIENT_TALLY_LABEL = 'Nutrients applied';
export const TASKS_GRID_NUTRIENT_RATE_UNIT = 'kg/ha';
export const TASKS_GRID_NUTRIENT_TALLY_EMPTY =
  'Add rates to soil inputs to see nutrients applied.';
export const TASKS_GRID_MOA_TALLY_LABEL = 'MoA groups';
export const TASKS_GRID_MOA_TALLY_EMPTY =
  'No MoA data for these products yet.';
export const TASKS_GRID_QUICK_ADD_ARIA_LABEL = 'Add a task';

// DateFieldEditor (components/common/DateFieldEditor) — typed entry and footer
export const DATE_FIELD_DAY_PLACEHOLDER = 'dd/mm/yyyy';
export const DATE_FIELD_MONTH_PLACEHOLDER = 'mm/yyyy';
export const DATE_FIELD_SET_MONTH_ONLY_LABEL = 'Set month only';
export const DATE_FIELD_SET_DAY_LABEL = 'Set day';
export const CLEAR_BUTTON_LABEL = 'Clear';

// Tasks grid — Application Details card
export const TASKS_GRID_APPLICATION_DETAILS_HEADING = 'Application details';
export const TASKS_GRID_FIELD_HAS_VALUE_SR_LABEL = '(has a value)';
export const TASKS_GRID_NOTES_FIELD_LABEL = 'Notes';
export const TASKS_GRID_NOTES_PLACEHOLDER = 'Add notes';
export const TASKS_GRID_WATER_RATE_FIELD_LABEL = 'Water rate';
export const TASKS_GRID_WATER_RATE_UNIT_LABEL = 'L/ha';
export const TASKS_GRID_WATER_RATE_PLACEHOLDER = 'Add water rate';
export const TASKS_GRID_TARGET_FIELD_LABEL = 'Target';
export const TASKS_GRID_TARGET_PLACEHOLDER = 'Add target, reason or pest';
export const TASKS_GRID_TARGET_ADD_PLACEHOLDER = 'Add target';

// Tasks grid — step sub-table inline edit placeholders
export const TASKS_GRID_SELECT_UNIT_PLACEHOLDER = 'Select unit';

// Tasks grid — accessible names
export const TASKS_GRID_EXPAND_STEPS_ARIA_LABEL = 'Expand steps';
export const TASKS_GRID_COLLAPSE_STEPS_ARIA_LABEL = 'Collapse steps';
export const TASKS_GRID_TASK_ACTIONS_ARIA_LABEL = 'Task actions';
export const TASKS_GRID_CLEAR_TIMING_ARIA_LABEL = 'Clear timing';
export const TASKS_GRID_STEP_ACTIONS_ARIA_LABEL = 'Input step actions';
// Combined with the step's own name, e.g. `${prefix} Urea`.
export const TASKS_GRID_REORDER_STEP_ARIA_LABEL_PREFIX = 'Re-order';
/** Marks controls (the row grips) that may take focus without ending step reorder mode. */
export const STEP_REORDER_CONTROL_ATTR = 'data-step-reorder-control';

// Tasks grid — empty state
export const TASKS_GRID_EMPTY_STATE_TITLE = 'No tasks added';
export const TASKS_GRID_EMPTY_STATE_HINT_PREFIX = 'Add a task with the';
export const TASKS_GRID_EMPTY_STATE_HINT_BUTTON_LABEL = '+';
export const TASKS_GRID_EMPTY_STATE_HINT_SUFFIX = 'buttons above.';

// Tasks grid — category filter footer message
export const TASKS_GRID_HIDDEN_INPUT_SINGULAR = 'input';
export const TASKS_GRID_HIDDEN_INPUT_PLURAL = 'inputs';
export const TASKS_GRID_HIDDEN_BY_FILTER_SUFFIX = 'hidden by this filter';

// Tasks grid — step type / task type mismatch
export const TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE =
  'This step type is not allowed for this task type.';

// Production task — Low/Medium/High yield and price matrix (OP-2128)
export const PRODUCTION_MATRIX_COLUMN_LABEL_SCENARIO = 'Scenario';
export const PRODUCTION_MATRIX_COLUMN_LABEL_YIELD = 'Yield (t/ha)';
export const PRODUCTION_MATRIX_COLUMN_LABEL_PRICE = 'Price ($/t)';
export const PRODUCTION_MATRIX_ROW_LABEL_LOW = 'Low';
export const PRODUCTION_MATRIX_ROW_LABEL_MEDIUM = 'Medium';
export const PRODUCTION_MATRIX_ROW_LABEL_HIGH = 'High';
export const PRODUCTION_MATRIX_REQUIRED_PLACEHOLDER = 'Required';
export const PRODUCTION_CROP_RESOLVING_TOOLTIP =
  'Loading crop and unit details for this plan...';

// ViewHeader (OP-2390) — one short purpose line per view, under the lens switcher.
export const VIEW_PURPOSE_INSIGHTS_SUMMARY =
  'Totals, comparisons and input quantities for the plans in scope';
export const VIEW_PURPOSE_INSIGHTS_ANALYSIS =
  'Compare plans side by side on income, costs and gross margin';
export const VIEW_PURPOSE_INSIGHTS_MARGIN_BREAK_EVEN =
  'Returns and break-even across yield and price scenarios';
export const VIEW_PURPOSE_INSIGHTS_NUTRITION =
  'Nutrients applied and removed across seasons, and whether each paddock is building or declining';
export const VIEW_PURPOSE_INSIGHTS_CASHFLOW =
  'When money goes out and comes in across the year';
export const VIEW_PURPOSE_DIAGNOSTICS_ROTATION_EFFICIENCY =
  'Which paddocks turn rainfall and inputs into the most yield and margin';
export const VIEW_PURPOSE_DIAGNOSTICS_WUE_TREND =
  'How much yield each millimetre of rain produces, year on year';
export const VIEW_PURPOSE_DIAGNOSTICS_FINANCIAL_EFFICIENCY =
  'How income and cost per hectare change year on year';
export const VIEW_PURPOSE_DIAGNOSTICS_BIO_VS_FINANCIAL =
  'Biological and financial efficiency side by side';

// Farm Diagnostics — Yearly Overview Total row (OP-2427)
export const FARM_DIAGNOSTICS_INCOME_UNIT_SUFFIX = '($/ha)';
export const FARM_DIAGNOSTICS_TOTAL_ROW_AVG_LABEL = 'avg';
export const FARM_DIAGNOSTICS_TOTAL_ROW_AVG_TOOLTIP =
  'Area-weighted average across the years shown';

// Links between Live totals, Insights and Farm diagnostics (OP-2391)
export const LINK_FULL_SENSITIVITY_LABEL = 'Full sensitivity';
export const LINK_ALL_CROPS_WHOLE_FARM_LABEL = 'All crops / whole farm';
export const LINK_OPEN_PLAN_LABEL = 'Open plan';
export const LINK_EFFICIENCY_TRENDS_LABEL = 'Efficiency trends';
export const LINK_MULTI_YEAR_TREND_LABEL = 'Multi-year trend';
export const SUMMARY_INPUT_SUMMARIES_SECTION_ID = 'summary2-inputSummaries';
